import React, { useMemo } from 'react';
import { ArrowDown, ArrowUp, Info, MinusCircle, Plus } from 'lucide-react';
import { Box, Button, Divider, Typography, useTheme } from '@mui/material';

import Autocomplete from 'components/autocomplete';
import { FeedProgramOption } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-fish-group.type';
import { FishGroupSummary } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-result.type';
import { useR2ScenarioState } from 'views/bluviu-nutrition/features/scenarios-v2/context/r2-scenario-context';
import { useScenarioResultState } from 'views/bluviu-nutrition/features/scenarios-v2/context/scenario-result-context';
import { buildFishGroupStats } from 'views/bluviu-nutrition/features/scenarios-v2/utils/fish-group-utils';
import FormatMetricValue from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-result-tab/format-metric-value';

type Props = {
  item: FishGroupSummary;
};

const ScenarioFishGroupProgram: React.FC<Props> = ({ item }) => {
  const theme = useTheme();
  const {
    selectedDefaultProgramName,
    getScenarioPostProcessQuery: { data: postProcessData },
    overrideFeedProgram,
    setOverrideFeedProgram,
  } = useScenarioResultState();
  const { r2ScenarioQuery: { data: scenario } } = useR2ScenarioState();

  const { isOverriding, program: selectedDefaultProgram } = overrideFeedProgram[item.fishGroupId] ?? { program: null, isOverriding: false };

  const fishGroupProgramSummaries = (postProcessData?.fishGroupSummaries ?? []).filter((s) => s.fishGroupId === item.fishGroupId);

  const referenceSummary = fishGroupProgramSummaries.find((s) => s.feedPlan === selectedDefaultProgramName) ?? item;

  const overrideOptions: FeedProgramOption[] = Array.from(
    new Map(
      fishGroupProgramSummaries
        .filter((s) => s.feedPlan !== selectedDefaultProgramName)
        .map((s) => [s.feedPlan, { id: s.feedPlan, name: s.feedPlan }] as const)
    ).values()
  );

  const overrideSummary = selectedDefaultProgram ? fishGroupProgramSummaries.find((s) => s.feedPlan === selectedDefaultProgram) : undefined;

  const selectedOverrideProgram = useMemo(
    () => overrideOptions.find((o) => o.id === selectedDefaultProgram) ?? null,
    [overrideOptions, selectedDefaultProgram]
  );

  const stats = buildFishGroupStats(referenceSummary, overrideSummary);
  const currency = scenario?.financialData?.currency;
  const hasOverride = !!overrideSummary;

  const handleRemoveOverride = () => {
    setOverrideFeedProgram((prev) => ({
      ...prev,
      [item.fishGroupId]: { program: null, isOverriding: false },
    }));
  };

  return (
    <Box
      height={'100%'}
      borderRadius={2}
      display={'flex'}
      gap={theme.spacing(10)}
      flexDirection={'column'}
      padding={theme.spacing(5)}
      bgcolor={theme.palette.lightBlue.o25}>
      <Box display="flex" alignItems="flex-start" justifyContent="space-between">
        <Box display="flex" flexDirection="column" gap={1}>
          <Typography variant="body2">Default program:</Typography>
          <Typography variant="subtitle2" fontWeight="bold">
            {referenceSummary.feedPlan}
          </Typography>
        </Box>
        {isOverriding ? (
          <Box display="flex" flexDirection="column" alignItems="flex-end" gap={1}>
            <Autocomplete
              dark
              options={overrideOptions}
              value={selectedOverrideProgram}
              disableCloseOnSelect={false}
              label="Override with"
              getOptionLabel={(option) => option.name}
              onChange={(_, value) =>
                setOverrideFeedProgram((prev) => ({
                  ...prev,
                  [item.fishGroupId]: { ...(prev[item.fishGroupId] ?? { isOverriding: true }), program: value ? value.id : null },
                }))
              }
            />
            <Button variant="text" color="primary" endIcon={<MinusCircle size={20} />} onClick={handleRemoveOverride}>
              Remove override
            </Button>
          </Box>
        ) : (
          <Button
            variant="outlined"
            size="small"
            disabled={overrideOptions.length === 0 || !!scenario?.appliedAs}
            endIcon={<Plus size={20} />}
            onClick={() =>
              setOverrideFeedProgram((prev) => ({
                ...prev,
                [item.fishGroupId]: { ...(prev[item.fishGroupId] ?? { program: null }), isOverriding: true },
              }))
            }>
            Override program
          </Button>
        )}
      </Box>
      <Box display="flex" flexDirection="column" gap={6}>
        {!isOverriding && (
          <Box display="flex" alignItems="center" gap={2}>
            <Info size={17} color={theme.palette.text.secondary} />
            <Typography variant="body2" color={theme.palette.text.secondary}>
              Add override to compare and set as new selected program for fish group.
            </Typography>
          </Box>
        )}
        <Divider flexItem />
        <Box display="flex" flexDirection="column" gap={3}>
          <Box display="flex">
            <Typography variant="body2" fontWeight="bold" sx={{ flex: 2 }}>
              Results for:
            </Typography>
            <Typography variant="body2" fontWeight="bold" sx={{ flex: 1, textAlign: 'right' }}>
              {referenceSummary.feedPlan}
            </Typography>
            {hasOverride && (
              <Typography variant="body2" fontWeight="bold" sx={{ flex: 1, textAlign: 'right' }}>
                {overrideSummary?.feedPlan}
              </Typography>
            )}
            {hasOverride && <Box sx={{ width: 100 }} />}
          </Box>

          {stats.map((stat) => (
            <Box key={stat.label} display="flex" alignItems="center">
              <Typography variant="body2" fontWeight="bold" sx={{ flex: 2, }}>
                {stat.label}
              </Typography>
              <Typography variant="body2" sx={{ flex: 1, alignItems: 'flex-end', display: 'flex', justifyContent: 'flex-end' }}>
                <FormatMetricValue value={stat.refValue} valueKey={stat.valueKey} category={stat.category} currency={currency} />
              </Typography>
              {hasOverride && (
                <Typography variant="body2" sx={{ flex: 1 }}>
                  <FormatMetricValue value={stat.overrideValue} valueKey={stat.valueKey} category={stat.category} currency={currency} />
                </Typography>
              )}
              {hasOverride && (
                <Box display="flex" alignItems="center" gap={1} sx={{ width: 100, justifyContent: 'flex-end' }}>
                  {stat.delta != null && stat.delta !== 0 && (
                    <>
                      <Typography variant="body2" color={'text.secondary'}>
                        ({stat.delta > 0 ? '+' : ''}
                        <FormatMetricValue value={stat.delta} valueKey={stat.valueKey} category={stat.category} currency={currency} />)
                      </Typography>
                      {stat.delta > 0 ? (
                        <ArrowUp size={14} color={theme.palette.success.main} />
                      ) : (
                        <ArrowDown size={14} color={theme.palette.error.main} />
                      )}
                    </>
                  )}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default React.memo(ScenarioFishGroupProgram);
